'use client';

import { Checkbox } from '@/components/inputs/Checkbox';
import { useListReferralsQuery } from '@/hooks/api/useListReferralsQuery';
import { EMAIL_REGEX } from '@/utils/emailRegex';
import { TrackingHelper } from '@/utils/tracking';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { useWizardContext } from '../../hooks/useWizardContext';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { createLead } from '../../services/LeadService';
import type { ContactDetails } from '../../types';
import { SelectInput } from '../shared/SelectInput';
import { StepLayout } from '../shared/StepLayout';
import { TextInput } from '../shared/TextInput';

const OTHER_REFERRAL_VALUE = 'Other';

/**
 * Validates a US phone number format.
 * Accepts: (123) 456-7890, 123-456-7890, 1234567890, +1 123 456 7890
 */
const isValidPhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned[0] === '1');
};

/**
 * Contact information step - collects basic contact details.
 */
export const ContactInfoStep = () => {
  const t = useTranslations('ReferralSourcePage');
  const { state, dispatch } = useWizardContext();
  const { goToNextStep, goToPreviousStep, hasPreviousStep } =
    useWizardNavigation();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const {
    data: referrals,
    //isPending: isLoadingReferrals,
    //error: referralsError,
  } = useListReferralsQuery();

  const options = [
    ...(referrals ?? [])
      .filter(referral => referral !== OTHER_REFERRAL_VALUE)
      .map(referral => ({ value: referral, label: referral })),
    { value: OTHER_REFERRAL_VALUE, label: t('otherOption') },
  ];

  // Initialize form state from wizard state (session is already loaded at this point)
  const [formData, setFormData] = useState<ContactDetails>(() => {
    const contact = state.formData?.contact_details;
    if (contact) {
      return {
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        hdyhau: contact.hdyhau,
        hdyhau_other: contact.hdyhau_other,
        sms_opt_in: contact.sms_opt_in,
      };
    }
    return {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    };
  });

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    },
    [errors]
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    },
    [errors]
  );
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.hdyhau?.trim() == 'Other' && !formData.hdyhau_other?.trim()) {
      newErrors.hdyhau_other = 'How did you hear about us is required';
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      // Update wizard state with contact details
      dispatch({
        type: 'SET_FORM_DATA',
        payload: { contact_details: formData },
      });

      // Create lead if we don't already have one
      if (!state.leadId) {
        try {
          dispatch({ type: 'SET_SAVING', payload: true });
          dispatch({ type: 'SET_ERROR', payload: null });

          const trackingData = TrackingHelper.getTrackingData();

          const leadId = await createLead(
            {
              ...state.formData,
              contact_details: formData,
            },
            trackingData
          );

          dispatch({ type: 'SET_LEAD_ID', payload: leadId });
        } catch (error) {
          console.error('Error creating lead:', error);
          dispatch({
            type: 'SET_ERROR',
            payload: 'Failed to save your information. Please try again.',
          });
          return;
        } finally {
          dispatch({ type: 'SET_SAVING', payload: false });
        }
      }

      // Navigate to next step
      goToNextStep();
    },
    [
      formData,
      validateForm,
      dispatch,
      goToNextStep,
      state.leadId,
      state.formData,
    ]
  );

  const showOtherInput = formData.hdyhau == OTHER_REFERRAL_VALUE;

  return (
    <StepLayout
      heading="You're almost there!"
      description={
        <>
          Get your personalized quote in seconds, with a copy sent directly to
          your inbox.
          <br className='hidden xl:inline' />
          We respect your privacy. Your information will never be shared.
        </>
      }
      submitLabel='Finish Details'
      onSubmit={handleSubmit}
      onBack={goToPreviousStep}
      showBack={hasPreviousStep}
      isSubmitting={state.isSaving}
    >
      <div className='flex flex-col gap-2.5'>
        {/* Error message */}
        {state.error && (
          <div className='rounded-md bg-red-50 p-3 text-sm text-red-600'>
            {state.error}
          </div>
        )}

        {/* First Name */}
        <div>
          <TextInput
            type='text'
            id='first_name'
            name='first_name'
            value={formData.first_name}
            onChange={handleInputChange}
            error={errors.first_name}
            placeholder='First Name'
          />
          {errors.first_name && (
            <p className='mt-1 text-sm text-red-500'>{errors.first_name}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <TextInput
            type='text'
            id='last_name'
            name='last_name'
            value={formData.last_name}
            onChange={handleInputChange}
            error={errors.last_name}
            placeholder='Last Name'
          />
          {errors.last_name && (
            <p className='mt-1 text-sm text-red-500'>{errors.last_name}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <TextInput
            type='email'
            id='email'
            name='email'
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            placeholder='Email'
          />
          {errors.email && (
            <p className='mt-1 text-sm text-red-500'>{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <TextInput
            type='tel'
            id='phone'
            name='phone'
            value={formData.phone}
            onChange={handleInputChange}
            error={errors.phone}
            placeholder='Phone Number'
          />
          {errors.phone && (
            <p className='mt-1 text-sm text-red-500'>{errors.phone}</p>
          )}
        </div>

        {/* HDYHAU */}
        <div>
          <SelectInput
            type='tel'
            id='hdyhau'
            name='hdyhau'
            value={formData.hdyhau}
            onChange={handleSelectChange}
            error={errors.hdyhau}
            placeholder='How did you hear about us?'
          >
            <option key={'Please select an option'} value=''>
              How did you hear about us?
            </option>
            {options.map(x => (
              <option key={x.value} value={x.value}>
                {x.label}
              </option>
            ))}
          </SelectInput>
          {errors.hdyhau && (
            <p className='mt-1 text-sm text-red-500'>{errors.hdyhau}</p>
          )}
        </div>

        {showOtherInput && (
          <div>
            <TextInput
              type='text'
              id='hdyhau_other'
              name='hdyhau_other'
              value={formData.hdyhau_other}
              onChange={handleInputChange}
              error={errors.hdyhau_other}
              placeholder='How did you hear about us?'
            />

            {errors.hdyhau_other && (
              <p className='mt-1 text-sm text-red-500'>{errors.hdyhau_other}</p>
            )}
          </div>
        )}

        {/* SMS Consent */}
        <div>
          <Checkbox
            id='sms_opt_in'
            name='sms_opt_in'
            checked={formData.sms_opt_in ?? false}
            onChange={handleInputChange}
            label={
              <span className='text-sh-dark-gray text-xs leading-[130%] font-normal'>
                By checking this box, I consent to receive marketing, customer
                care, public service announcements, and account notifications
                from Sollis Health. Reply STOP to opt-out; Reply HELP for
                support; Message & data rates may apply; Messaging frequency may
                vary. Visit our{' '}
                <Link
                  href='https://www.sollishealth.com/privacy'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-[#3366cc] underline'
                >
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link
                  href='https://www.sollishealth.com/legal'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-[#3366cc] underline'
                >
                  Terms of Service
                </Link>{' '}
                for more information.
              </span>
            }
          />
          {errors.sms_opt_in && (
            <p className='mt-1 text-sm text-red-500'>{errors.sms_opt_in}</p>
          )}
        </div>

        {/* Privacy and TOS */}
        <div>
          <p>
            By clicking &quot;Finish Details&quot;, you are agreeing to our{' '}
            <Link
              href='https://www.sollishealth.com/privacy'
              target='_blank'
              rel='noopener noreferrer'
              className='text-[#3366cc] underline'
            >
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              href='https://www.sollishealth.com/legal'
              target='_blank'
              rel='noopener noreferrer'
              className='text-[#3366cc] underline'
            >
              Terms of Service
            </Link>{' '}
            and agree to receive occasional email communications from Sollis.
            You can unsubscribe at any time.
          </p>
        </div>
      </div>
    </StepLayout>
  );
};
